import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Input,
  Row,
  Spin,
  message,
} from "antd";
import { AxiosResponse } from "axios";
import React, { CSSProperties, Component, Fragment, RefObject } from "react";
import AssociatedEntitiesSection from "../../components/AssociatedEntitiesSection";
import CardSection from "../../components/CardSection";
import DisplayTag from "../../components/DisplayTag";
import BaseForm from "../../components/Form/BaseForm";
import StepContainer from "../../components/StepContainer";
import {
  APIError,
  AddressInterface,
  AssociatedAddress,
  AssociatedEmail,
  AssociatedPhone,
  BranchMap,
  Company,
  CompanyContact,
  ContactLevel,
  Country,
  Customer,
  CustomerCompanyAssociation,
  CustomerCompanyAssociationContactLevel,
  Director,
  EmailInterface,
  FinancialInstitution,
  FormFieldConfig,
  Opportunity,
  PhoneInterface,
  SelectOptionsInterface,
  Subscription,
  Title,
  UpdateCompanyAddressFnProps,
  UpdateCompanyEmailFnProps,
  UpdateCompanyPhoneFnProps,
  UpdatePaymentInformationFnProps,
  User,
} from "../../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  convertCustomersToSelectOptions,
  extractFileName,
  generateCompanySegments,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
  responseHandlerForMutationsThatDoNotExposeErrors,
  subscriptionInitialTags,
  isAutomaticTag,
} from "../../helpers/utilities";

import * as Sentry from "@sentry/browser";
import { FormInstance } from "antd/lib/form";

import debounce from "lodash.debounce";
import moment from "moment";
import { FieldData } from "rc-field-form/lib/interface";
import { NavLink } from "react-router-dom";
import CompanyOnboardingDetails from "../../components/CompanyOnboardingDetails";
import ModalCreator from "../../components/ModalCreator";
import {
  ASSIGN_ADDRESS_TO_COMPANY,
  ASSIGN_EMAIL_TO_COMPANY,
  ASSIGN_PHONE_TO_COMPANY,
  ASSIGN_TAGS_TO_COMPANY,
  ASSIGN_TAGS_TO_COMPANY_SUBSCRIPTION,
  CREATE_COMPANY_DIRECTOR,
  CREATE_CONTACT_PERSON_MUTATION,
  CREATE_DIRECTOR_CONTACT,
  DELETE_COMPANY_CONTACT_PERSON_MUTATION,
  DELETE_CONTACT_PERSON_MUTATION,
  DELETE_DIRECTOR_CONTACT,
  GET_COMPANY_CONTACT_PERSONS,
  GET_COMPANY_DIRECTORS,
  MARK_COMPANY_ADDRESS_AS_PRIMARY,
  MARK_COMPANY_EMAIL_AS_PRIMARY,
  MARK_COMPANY_PHONE_AS_PRIMARY,
  REMOVE_DIRECTOR,
  UNASSIGN_ADDRESS_FROM_COMPANY,
  UNASSIGN_EMAIL_FROM_COMPANY,
  UNASSIGN_PHONE_FROM_COMPANY,
  UPDATE_COMPANY_ADDRESS,
  UPDATE_COMPANY_CONTACT_PERSON_MUTATION,
  UPDATE_COMPANY_EMAIL,
  UPDATE_COMPANY_MUTATION,
  UPDATE_COMPANY_PHONE,
  UPDATE_CONTACT_PERSON_MUTATION,
} from "../../gql/companies";
import {
  ASSIGN_CUSTOMER_TO_COMPANY_MUTATION,
  LOOKUP_USER,
  SEARCH_CUSTOMER,
  UPDATE_CUSTOMER_COMPANY_ASSOCIATION_EXTENDED,
} from "../../gql/customers";
import {
  GET_FINANCIAL_INSTITUTIONS,
  GET_INDUSTRY_SECTORS,
  GET_TAGS,
  GET_TITLES,
  GET_TRUSTEES_SUMMARY,
  VALIDATE_PHONE_NUMBER_QUERY,
} from "../../gql/general";
import { GET_CONTACT_LEVELS } from "../../gql/templates";
import { GET_ROLE_SUMMARY } from "../../gql/roles";
import { DEBOUNCE_TIME, MANUAL_TAG_NAMES } from "../../helpers/constants";


const { Panel } = Collapse;

export interface ContactPersonInterface extends Customer {
  influenceLevel: string;
  isEmployee: boolean;
  staffId?: string;
}

const relationshipManagerRoleId = "29";

const customerFieldsToBeValidated: string[] = [
  "relationship",
  "staffId",
  "firstName",
  "lastName",
  "gender",
  "countryId",
  "email",
  "gender",
  "phone",
  "influenceLevel",
  "isEmployee",
  "lookupPhoneNumber",
  "dateOfEmployment",
  "contactLevelIds",
  "titleId",
];

const contactPersonFieldsToValidate = [
  "emailAddress",
  "mobileNumber",
  "contactName",
  "contactLevelIds",
  "customerId",
  "isExistingCustomer",
  "role",
  "documentUrl"
];

const directorFieldsToValidate = [
  "firstName",
  "lastName",
];

const directorContactFieldsToValidate = [
  "emailAddress",
  "phoneNumber",
];

enum mapServerPropToLocalProp {
  email = "address",
  streetName = "streetName",
  countryId = "countryId",
  number = "phone",
  phone = "number",
  tinNumber = "tinNumber",
  relationship_manager = "relationship manager",
}

interface Props {
  setPercentage: (percentage: number) => void;
  countries: Country[];
  company: Company;
  next: () => void;
  prev: () => void;
  getNextViewName: () => string | null;
  getPreviousViewName: () => string | null;
  save?: () => void;
  editingState?: boolean;
  opportunity: Opportunity;
  loadOpportunity: () => void;
  showOnboardingDetailsSection?: boolean;
  updateCompanyPaymentInformation: (
    props: UpdatePaymentInformationFnProps,
  ) => void;
  subscriptions?: Subscription[];
  companyContacts?: CompanyContact[];
  cancelEdit?: () => void;
  isEditing: boolean;
}

interface State {
  isLoading: boolean;
  phoneList: AssociatedPhone[];
  emailList: AssociatedEmail[];
  addressList: AssociatedAddress[];
  contactPerson?: Customer;
  showForm: boolean;
  currentContactPerson: Customer | null;
  contactPersons: Customer[];
  lookupPhoneNumber?: string;
  idToBeDeleted?: string;
  showDeleteConfirmationModal: boolean;
  industrySectors: SelectOptionsInterface[];
  existingUserFound: boolean;
  customerCompanyAssociations: CustomerCompanyAssociation[];
  customerCompanyAssociationBeingEdited?: CustomerCompanyAssociation;
  trustees: SelectOptionsInterface[];
  companyAccountTags: SelectOptionsInterface[];
  productTags: SelectOptionsInterface[];
  showTagDateModal: boolean;
  pendingTagIds: string[] | null;
  tagEffectiveDate: moment.Moment | null;
  tagNotes: string;
  financialInstitutions: FinancialInstitution[];
  branchMap: BranchMap;
  titles: Title[];
  contactLevels: ContactLevel[];
  users: User[];
  userSearchTerm: string;
  currentCompanyContact?: CompanyContact;
  customers: Customer[];
  customerSearchTerm: string;
  companyContactPersons: CompanyContact[];
  companyContactIdSelected?: string;
  companyContactFieldsToHide: string[];
  isExistingCustomer: boolean;
  companyDirectors: Director[];
  currentCompanyDirector?: Director;
  showDirectorContactForm: boolean;
  directorIdSelected?: string;
  showDeleteDirectorConfirmationModal: boolean;
  documentUrl: string;
  contactPersonRequired: boolean;
  showCompanyContactModal: boolean;
}

export function customerBioFieldsCreator({
  countries,
  initialValue,
  lookupPhoneNumber,
  titles,
  contactLevels,
}: {
  countries: Country[];
  titles: Title[];
  initialValue?: CustomerCompanyAssociation;
  lookupPhoneNumber?: string;
  contactLevels: ContactLevel[];
}): FormFieldConfig[] {
  const customer = initialValue?.customer;
  const customerEmail = customer?.customerEmails;
  let contactLevelsIds: string[] = [];
  if (
    initialValue &&
    initialValue.customerCompanyAssociationContactLevels.length > 0
  ) {
    const associationLevelsArray =
      initialValue.customerCompanyAssociationContactLevels.map(
        (cclevel: CustomerCompanyAssociationContactLevel) =>
          cclevel.contactLevel.id,
      );
    contactLevelsIds = associationLevelsArray;
  }
  return [
    {
      id: "titleId",
      name: "titleId",
      inputType: "select",
      rule: "string",
      required: false,
      placeholder: "Title",
      label: "Title",
      selectOptions: titles,
      initialValue: customer?.title
        ? capitalizeFirstLetter(customer.title.name)
        : undefined,
      colConfig: {
        span: 7,
      },
    },
    {
      id: "firstName",
      name: "firstName",
      inputType: "string",
      rule: "string",
      placeholder: "First Name",
      label: "First Name",
      initialValue: customer?.firstName,
      required: true,
      colConfig: {
        offset: 1,
        span: 7,
      },
    },
    {
      id: "lastName",
      name: "lastName",
      inputType: "string",
      rule: "string",
      placeholder: "Last Name",

      label: "Last Name",
      initialValue: customer?.lastName,

      required: true,
      colConfig: {
        offset: 1,
        span: 7,
      },
    },
    {
      id: "countryId",
      name: "countryId",
      inputType: "select",
      rule: "string",
      required: true,
      placeholder: "Nationality",
      label: "Nationality",
      initialValue: customer?.country?.id,

      selectOptions: countries,
      colConfig: {
        span: 7,
      },
    },

    {
      id: "email",
      name: "email",
      inputType: "string",
      rule: "email",
      label: "Email",
      placeholder: "Email Address",
      disabled: initialValue ? true : false,
      initialValue:
        customerEmail && customerEmail.length
          ? customerEmail[0].email.address
          : "",
      required: false,
      colConfig: {
        offset: 1,
        span: 7,
      },
    },

    {
      id: "gender",
      name: "gender",
      inputType: "select",
      rule: "string",
      placeholder: "Gender",
      label: "Gender",
      initialValue: customer?.gender || undefined,
      selectOptions: [
        { id: "0", name: "Male", value: "male" },
        { id: "1", name: "Female", value: "female" },
      ],
      required: true,
      colConfig: {
        offset: 1,
        span: 7,
      },
    },

    {
      id: "relationship",
      name: "relationship",
      inputType: "string",
      rule: "string",
      placeholder: "Relationship To Company",
      label: "Relationship",
      initialValue: initialValue?.relationship,
      required: false,
      colConfig: {
        span: 7,
      },
    },

    {
      id: "staffId",
      name: "staffId",
      inputType: "string",
      rule: "string",
      placeholder: "Staff ID",
      label: "Staff ID",
      initialValue: initialValue?.staffId,
      required: false,
      colConfig: {
        span: 7,
      },
    },

    {
      id: "influenceLevel",
      name: "influenceLevel",
      inputType: "select",
      rule: "string",
      placeholder: "Level Of Influence",
      label: "Level Of Influence",
      initialValue: initialValue?.influenceLevel,
      selectOptions: [
        { id: "0", name: "Low", value: "low" },
        { id: "1", name: "Medium", value: "medium" },
        { id: "2", name: "High", value: "high" },
      ],
      required: false,
      colConfig: {
        span: 7,
        offset: 1,
      },
    },
    {
      id: "contactLevelIds",
      name: "contactLevelIds",
      inputType: "multipleSelect",
      rule: "string",
      isArrayField: true,
      placeholder: "Contact Level",
      label: "Contact Level",
      initialValue: contactLevelsIds,
      selectOptions: contactLevels,
      required: true,
      colConfig: {
        span: 7,
        offset: 1,
      },
    },

    {
      id: "isEmployee",
      name: "isEmployee",
      inputType: "checkbox",
      valuePropName: "checked",
      rule: "boolean",
      label: "Employment Status",
      value: "Is this person an employee?",
      initialValue: initialValue?.isEmployee,
      required: false,
      colConfig: {
        span: 7,
      },
    },

    {
      id: "dateOfEmployment",
      name: "dateOfEmployment",
      inputType: "defaultDate",
      rule: "object",
      placeholder: "Date Of Employment",
      label: "Date Of Employment",
      initialValue:
        initialValue?.dateOfEmployment &&
        moment(initialValue?.dateOfEmployment),
      required: false,
      colConfig: {
        offset: 1,
        span: 7,
      },
    },

    {
      id: "phone",
      name: "phone",
      inputType: "phone",
      rule: "string",
      label: "Phone",
      placeholder: "Phone",
      required: true,
      disabled: true,
      elementStyle: { width: "100%" },
      initialValue:
        lookupPhoneNumber || customer?.activeCustomerPhones[0]?.phone?.number,
      colConfig: {
        offset: 1,
        span: 7,
      },
    },
  ];
}

export function ContactPerson(
  contactPerson: ContactPersonInterface,
  style?: CSSProperties,
): JSX.Element {
  const phoneType = contactPerson.activeCustomerPhones[0]?.phone.type;
  const phoneNumber = contactPerson.activeCustomerPhones[0]?.phone.number;
  const email =
    contactPerson.customerEmails.length > 0 &&
    contactPerson.customerEmails[0].email.address;
  const divStyle = style || {
    padding: "18px",
    boxSizing: "border-box",
    border: "1px solid rgb(232, 232, 232)",
    marginBottom: "10px",
  };

  return (
    <div key={contactPerson.id} style={divStyle}>
      <CardSection headingText="Name" data={contactPerson.fullname} />
      <CardSection
        headingText="Phone"
        data={
          <div>
            <DisplayTag type={phoneType} />
            {phoneNumber}
          </div>
        }
      />
      {email && <CardSection headingText="Email" data={email} />}
    </div>
  );
}

export function CompanyContactPerson(
  contactPerson: CompanyContact,
  style?: CSSProperties,
  editButton?: JSX.Element,
): JSX.Element {
  const phoneNumber = contactPerson?.mobileNumber;
  const email = contactPerson?.emailAddress;
  const cLevels = contactPerson?.contactLevels?.map((cLevel) => {
    return cLevel.name;
  });
  const customer = contactPerson?.customer;
  const divStyle = style || {
    padding: "18px",
    boxSizing: "border-box",
    border: "1px solid rgb(232, 232, 232)",
    marginBottom: "10px",
  };
  return (
  <div key={contactPerson?.id} style={divStyle}>
    <CardSection headingText="Name" data={contactPerson?.contactName ?? "N/A"} />
    <CardSection headingText="Phone" data={phoneNumber ? <div>{phoneNumber}</div> : "N/A"} />
    <CardSection headingText="Email" data={email ?? "N/A"} />
    <CardSection headingText="Contact Levels" data={cLevels?.length ? cLevels.join(", ") : "N/A"} />
    <CardSection headingText="Role" data={contactPerson?.role ?? "N/A"} />
    <CardSection
      headingText="Customer"
      data={
        customer ? (
          <NavLink
            to={{
              pathname: `/customers/${customer.id}`,
              state: { type: "customer" },
            }}
          >
            {generateNameFromCustomer(customer)}
          </NavLink>
        ) : "N/A"
      }
    />
    <CardSection
      headingText="Attachment"
      data={
        contactPerson?.documentUrl ? (
          <a href={contactPerson.documentUrl} target="_blank" rel="noopener noreferrer">
            Click here to view
          </a>
        ) : "N/A"
      }
    />
    {editButton ?? <></>}
  </div>
);
}

export function CompanyDirector(
  director: Director,
  style?: CSSProperties,
  editButton?: JSX.Element,
  deleteDirectorContact?: (contact: string, directorId: string) => void
): JSX.Element {
  const {
    phones = [],
    emails = [],
    companies = [],
    firstName,
    lastName,
  } = director;

  const directorData = director as Director & {
    phoneNumbers?: Array<string | { number?: string | null } | null>;
    emailAddresses?: Array<string | { address?: string | null } | null>;
  };

  const phoneNumbers = Array.from(
    new Set(
      [
        ...phones.map((phone) => phone?.number).filter(Boolean),
        ...(directorData.phoneNumbers ?? []).map((phone) =>
          typeof phone === "string" ? phone : phone?.number,
        ),
      ].filter((phone): phone is string => !!phone),
    ),
  );

  const emailAddresses = Array.from(
    new Set(
      [
        ...emails.map((email) => email?.address).filter(Boolean),
        ...(directorData.emailAddresses ?? []).map((email) =>
          typeof email === "string" ? email : email?.address,
        ),
      ].filter((email): email is string => !!email),
    ),
  );

  const divStyle = style || {
    padding: '18px',
    boxSizing: 'border-box',
    border: '1px solid rgb(232, 232, 232)',
    marginBottom: '10px',
  };

  return (
    <div key={director.id} style={divStyle}>
      <CardSection headingText="Name" data={`${firstName} ${lastName}`} />

      {phoneNumbers.length > 0 && (
        <CardSection
          headingText="Phones"
          data={
            <div>
              {phoneNumbers.map((phone, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{phone}</span>
                  {deleteDirectorContact && <DeleteOutlined onClick={() => deleteDirectorContact?.(phone, director.id)} />}
                </div>
              ))}
            </div>
          }
        />
      )}

      {emailAddresses.length > 0 && (
        <CardSection
          headingText="Emails"
          data={
            <div>
              {emailAddresses.map((email, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{email}</span>
                  {deleteDirectorContact && <DeleteOutlined onClick={() => deleteDirectorContact?.(email, director.id)} />}
                </div>
              ))}
            </div>
          }
        />
      )}

      {companies.length > 0 && (
        <CardSection
          headingText="Companies"
          data={
            <div>
              {companies.map((company, index) => (
                <div key={index}>
                  <NavLink to={`/companies/${company.id}`}>
                    {company.name}
                  </NavLink>
                </div>
              ))}
            </div>
          }
        />
      )}

      {editButton || <></>}
    </div>
  );
}

export function ContactPersonWithAssociation(props: {
  customerCompanyAssociation: CustomerCompanyAssociation;
  setCustomerCompanyAssociationToBeEdited: (
    association: CustomerCompanyAssociation,
  ) => void;
  editingState?: boolean;
  toggleDeleteConfirmationModal: () => void;
  setIdToBeDeleted: (id: string) => void;
}): JSX.Element {
  const contactPerson: ContactPersonInterface = props.customerCompanyAssociation
    .customer as ContactPersonInterface;
  const relationship =
    props.customerCompanyAssociation.relationship &&
    props.customerCompanyAssociation.relationship;
  const isEmployee = props.customerCompanyAssociation.isEmployee ? "Yes" : "No";
  const dateOfEmployment =
    props.customerCompanyAssociation.dateOfEmployment &&
    props.customerCompanyAssociation.dateOfEmployment;

  const influenceLevel =
    props.customerCompanyAssociation.influenceLevel &&
    capitalizeFirstLetter(props.customerCompanyAssociation.influenceLevel);

  let contactLevel = "";
  if (
    props.customerCompanyAssociation.customerCompanyAssociationContactLevels
      .length > 0
  ) {
    const associationLevelsArray =
      props.customerCompanyAssociation.customerCompanyAssociationContactLevels.map(
        (cclevel: CustomerCompanyAssociationContactLevel) =>
          cclevel.contactLevel.name,
      );
    contactLevel = associationLevelsArray.join();
  }

  return (
    <div
      key={props.customerCompanyAssociation.id}
      style={{
        padding: "18px",
        boxSizing: "border-box",
        border: "1px solid rgb(232, 232, 232)",
        marginBottom: "10px",
      }}
    >
      {ContactPerson(contactPerson, {})}
      {relationship && (
        <CardSection
          headingText="Relationship To Company"
          data={relationship}
        />
      )}
      {contactLevel && (
        <CardSection headingText="Contact Level" data={contactLevel} />
      )}
      {influenceLevel && (
        <CardSection headingText="Level Of Influence" data={influenceLevel} />
      )}

      <CardSection headingText="Is Employee" data={isEmployee} />
      {dateOfEmployment && (
        <CardSection
          headingText="Date Of Employment"
          data={moment(dateOfEmployment).format("L")}
        />
      )}
      {props.editingState && (
        <Button
          id="phone-card-edit-btn"
          type="dashed"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            props.setCustomerCompanyAssociationToBeEdited(
              props.customerCompanyAssociation,
            );
          }}
        >
          <EditOutlined /> Edit
        </Button>
      )}
      <Button
        id="card-delete-btn"
        type="dashed"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          props.setIdToBeDeleted(props.customerCompanyAssociation.id);
          props.toggleDeleteConfirmationModal();
        }}
      >
        <DeleteOutlined />
        Delete
      </Button>
    </div>
  );
}

export function ContactPersonLookupResult(
  contactPerson: ContactPersonInterface,
  existingCustomerFormFields: FormFieldConfig[],
  reference: RefObject<FormInstance>,
): JSX.Element {
  return (
    <div
      key={contactPerson.id}
      style={{
        maxWidth: "405px",
        padding: "18px",
        boxSizing: "border-box",
        border: "1px solid rgb(232, 232, 232)",
        marginBottom: "10px",
      }}
    >
      {ContactPerson(contactPerson)}
      <BaseForm
        reference={reference}
        layout="vertical"
        fields={existingCustomerFormFields}
        fieldsToHide={[]}
      />
    </div>
  );
}

export interface CompanyContactData {
  emailAddress: string;
  mobileNumber: string;
  contactName: string;
  contactLevel: ContactLevel;
}

export default class CompanyDetails extends Component<Props, State> {
  _isMounted = false;
  reference: RefObject<FormInstance>;
  lookupFormReference: RefObject<FormInstance>;
  newContactPersonFormReference: RefObject<FormInstance>;
  newCompanyContactPersonFormReference: RefObject<FormInstance>;
  existingContactPersonFormReference: RefObject<FormInstance>;
  tagsReference: RefObject<FormInstance>;
  newCompanyDirectorFormReference: RefObject<FormInstance>;
  newCompanyDirectorContactFormReference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      financialInstitutions: [],
      idToBeDeleted: undefined,
      showDeleteConfirmationModal: false,
      isLoading: true,
      phoneList: [],
      addressList: [],
      emailList: [],
      showForm: false,
      currentContactPerson: null,
      contactPersons: [],
      industrySectors: [],
      existingUserFound: false,
      customerCompanyAssociations: [],
      trustees: [],
      titles: [],
      companyAccountTags: [],
      productTags: [],
      showTagDateModal: false,
      pendingTagIds: null,
      tagEffectiveDate: null,
      tagNotes: "",
      branchMap: {},
      contactLevels: [],
      users: [],
      userSearchTerm: "",
      customers: [],
      customerSearchTerm: "",
      companyContactPersons: [],
      companyContactFieldsToHide: ["customerId"],
      isExistingCustomer: false,
      companyDirectors: [],
      showDirectorContactForm: false,
      showDeleteDirectorConfirmationModal: false,
      documentUrl: "",
      contactPersonRequired: true,
      showCompanyContactModal: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.lookupFormReference = React.createRef<FormInstance>();
    this.newContactPersonFormReference = React.createRef<FormInstance>();
    this.newCompanyContactPersonFormReference = React.createRef<FormInstance>();
    this.existingContactPersonFormReference = React.createRef<FormInstance>();
    this.tagsReference = React.createRef<FormInstance>();
    this.newCompanyDirectorFormReference = React.createRef<FormInstance>();
    this.newCompanyDirectorContactFormReference = React.createRef<FormInstance>();

    this.next = this.next.bind(this);
  }

  setRef = (
    name:
      | "reference"
      | "lookupFormReference"
      | "newContactPersonFormReference"
      | "existingContactPersonFormReference"
      | "tagsReference"
      | "newCompanyDirectorFormReference"
      | "newCompanyDirectorContactFormReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  validatePhoneNumber = async (phoneNumber: string): Promise<boolean> => {
    interface Response {
      data: {
        validatePhoneNumber: boolean;
      };
    }

    let isValid = false;

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: VALIDATE_PHONE_NUMBER_QUERY,
        variables: { phoneNumber },
      });

      isValid = data.validatePhoneNumber;
    } catch (error) { }

    return isValid;
  };

  phoneAlreadyExists = (id: string): boolean => {
    const filter = (item: AssociatedPhone): boolean => item.id === id;
    return this.state.phoneList.some(filter);
  };

  emailAlreadyExists = (id: string): boolean => {
    const filter = (item: AssociatedEmail): boolean => item.id === id;
    return this.state.emailList.some(filter);
  };

  addressAlreadyExists = (id: string): boolean => {
    const filter = (item: AssociatedAddress): boolean => item.id === id;
    return this.state.addressList.some(filter);
  };

  updateCompanyAddress = async ({
    companyAddress,
    newlyCreatedAddressId,
  }: UpdateCompanyAddressFnProps): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        updateCompanyAddress: {
          companyAddress: {
            company: Company;
            address: AddressInterface;
          };
        };
      };
    }

    request({
      query: UPDATE_COMPANY_ADDRESS,
      variables: {
        companyAddressId: companyAddress.id,
        addressId: newlyCreatedAddressId,
        companyId: this.props.company.id,
      },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      if (data.updateCompanyAddress.companyAddress.company) {
        this.setState({
          isLoading: false,
          addressList:
            data.updateCompanyAddress.companyAddress.company.companyAddresses,
        });
      } else {
        message.error("Request Failed");
        this.setState({ isLoading: false });
      }
    });
  };

  handleIsExistingCustomerchecked = (value: boolean) => {
    if (!value) {
      this.setState({
        isExistingCustomer: value,
        companyContactFieldsToHide: [
          ...this.state.companyContactFieldsToHide,
          "customerId",
        ],
      });
    } else {
      this.setState({
        isExistingCustomer: value,
        companyContactFieldsToHide:
          this.state.companyContactFieldsToHide.filter(
            (item) => item !== "customerId",
          ),
      });
    }
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleContactLevelsChange = (value: string | string[], options: any | any[]): void => {
    if (window.location.pathname.split("/").includes("business-development")) {
      this.setState({
        contactPersonRequired: false,
      });
      return;
    }

    const optionsArray = Array.isArray(options) ? options : [options];
    const contactPersonRequired = !optionsArray.some(
      (option) => option.children === "RM" || option.children === "Client Services"
    );

    this.setState({
      contactPersonRequired,
    });
  };

  createCompanyContact = async (): Promise<void> => {
    interface Response {
      data: {
        createCompanyContact: {
          companyContact: CompanyContact;
          errors: APIError[];
        };
      };
    }
    let data;

    try {
      data =
        await this.newCompanyContactPersonFormReference?.current?.validateFields(
          contactPersonFieldsToValidate,
        );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    request({
      query: CREATE_CONTACT_PERSON_MUTATION,
      variables: {
        ...data,
        companyId: this.props.company.id,
        isExistingCustomer: this.state.isExistingCustomer,
        documentUrl: this.state.documentUrl,
      },
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        if (data?.createCompanyContact) {
          const { errors } = data.createCompanyContact;

          if (errors && errors.length > 0) {
            message.error(`${errors[0].property}: ${errors[0].description}`);
          } else {
            message.success("Contact person added successfully!");
            this.props.loadOpportunity();
            this.getCompanyContactPersons();
            this.newCompanyContactPersonFormReference?.current?.resetFields();
          }
        } else {
          message.error("Request Failed");
        }
      })
      .catch((error) => {
        message.error("Error Performing Request");
        Sentry.captureException(JSON.stringify(error, null, 4));
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };
  updateCompanyContact = async (): Promise<void> => {
    interface Response {
      data: {
        updateCompanyContact: {
          companyContact: CompanyContact;
          errors: APIError[];
        };
      };
    }

    try {
      const data =
        await this.newCompanyContactPersonFormReference?.current?.validateFields(
          contactPersonFieldsToValidate,
        );

      this.setState({ isLoading: true });

      request({
        query: UPDATE_COMPANY_CONTACT_PERSON_MUTATION,
        variables: {
          ...data,
          id: this.state.currentCompanyContact?.id,
          companyId: this.props.company.id,
          documentUrl: this.state.documentUrl,
        },
      })
        .then(({ data: { data } }: AxiosResponse<Response>): void => {
          if (data?.updateCompanyContact) {
            const { errors } = data.updateCompanyContact;

            if (errors && errors.length > 0) {
              message.error(`${errors[0].property} ${errors[0].description}`);
            } else {
              message.success("Contact person updated successfully!");
              this.setState({ currentCompanyContact: undefined });
            }

            this.props.loadOpportunity();
            this.getCompanyContactPersons();
          } else {
            message.error("Request Failed");
          }
        })
        .catch((error) => {
          message.error("Error Performing Request");
          Sentry.captureException(JSON.stringify(error, null, 4));
        })
        .finally(() => {
          this.setState({ isLoading: false });
        });
    } catch (error) {
      handleValidationErrors(error);
    }
  };

  deleteCompanyContact = async (): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      result: Boolean;
    }

    try {
      const response: AxiosResponse = await request({
        query: DELETE_COMPANY_CONTACT_PERSON_MUTATION,
        variables: { id: this.state.companyContactIdSelected },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "deleteCompanyContact",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        message.success("Contact person deleted successfully");
      }

      this.getCompanyContactPersons();
    } catch (error) {
      message.error(`Failed to delete contact person - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleDeleteConfirmationModal(undefined);
    }
  };

  getCompanyContactPersons = (): void => {
    this.setState({ isLoading: true });

    request({
      query: GET_COMPANY_CONTACT_PERSONS,
      variables: { companyId: this.props.company.id, page: "1" },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            companyContactPersons: {
              count: number;
              results: CompanyContact[];
              errors: object;
            };
          };
        }>): void => {
          if (data.data.companyContactPersons.results) {
            this.setState({
              companyContactPersons: data.data.companyContactPersons.results,
            });
          }
        },
      )
      .catch((error) => {
        message.error("Could Not Fetch Company Contacts");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  createCompanyDirector = async (): Promise<void> => {
    interface Response {
      data: {
        createCompanyDirector: {
          director: Director;
          errors: APIError[];
        };
      };
    }
    let data;

    try {
      data =
        await this.newCompanyDirectorFormReference?.current?.validateFields(
          directorFieldsToValidate,
        );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    request({
      query: CREATE_COMPANY_DIRECTOR,
      variables: {
        ...data,
        companyId: this.props.company.id,
      },
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        if (data?.createCompanyDirector) {
          const { errors } = data.createCompanyDirector;

          if (errors && errors.length > 0) {
            message.error(`${errors[0].property}: ${errors[0].description}`);
          } else {
            message.success("Director added successfully!");
          }

          this.getCompanyDirectors();
          this.newCompanyDirectorFormReference?.current?.resetFields();
        } else {
          message.error("Request Failed");
        }
      })
      .catch((error) => {
        message.error("Error Performing Request: ", error);
        Sentry.captureException(JSON.stringify(error, null, 4));
      })
      .finally(() => this.setState({ isLoading: false }));
  };

  deleteCompanyDirector = async (): Promise<void> => {
    const { directorIdSelected } = this.state;

    interface Response {
      directors: Director[];
      errors: APIError[];
    };

    this.setState({
      isLoading: true,
    });

    try {
      const response: AxiosResponse = await request({
        query: REMOVE_DIRECTOR,
        variables: {
          directorId: directorIdSelected,
          companyId: this.props.company.id
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "removeCompanyDirector",
      );

      if (mutation?.directors) {
        const updatedList = this.state.companyDirectors.filter(
          (director: Director) => {
            return director.id !== directorIdSelected;
          },
        );

        this.setState({ companyDirectors: updatedList });
      } else {
        message.error("Failed to delete director");
      }
    } catch (error) {
      message.error(`Failed to delete director - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false, showDeleteDirectorConfirmationModal: false });
    }
  };

  deleteDirectorContact = async (contact: string, directorId: string): Promise<void> => {
    interface Response {
      director: Director;
      errors: APIError[];
    }

    this.setState({ isLoading: true });

    const isEmail = contact.includes('@');

    try {
      const response: AxiosResponse<Response> = await request({
        query: DELETE_DIRECTOR_CONTACT,
        variables: {
          directorId,
          emailAddress: isEmail ? contact : undefined,
          phoneNumber: isEmail ? undefined : contact,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "removeDirectorContact",
      );
      if (mutation?.director) {
        this.getCompanyDirectors();
        message.success("Contact removed successfully");
      } else {
        message.error("Failed to delete director contact");
      }
    } catch (error) {
      message.error(`Failed to delete director contact - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createCompanyDirectorContact = async (): Promise<void> => {
    interface Response {
      data: {
        addDirectorContact: {
          director: Director;
          errors: APIError[];
        };
      };
    }
    let data;

    try {
      data =
        await this.newCompanyDirectorContactFormReference?.current?.validateFields(
          directorContactFieldsToValidate,
        );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    request({
      query: CREATE_DIRECTOR_CONTACT,
      variables: {
        ...data,
        directorId: this.state.currentCompanyDirector?.id,
      },
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        if (data?.addDirectorContact.director) {
          const { errors } = data.addDirectorContact;

          if (errors && errors.length > 0) {
            message.error(`${errors[0].property}: ${errors[0].description}`);
          } else {
            message.success("Contact added successfully!");
          }

          this.getCompanyDirectors();
          this.newCompanyDirectorContactFormReference?.current?.resetFields();
        } else {
          message.error("Request Failed");
        }
      })
      .catch((error) => {
        message.error("Error Performing Request: ", error);
        Sentry.captureException(JSON.stringify(error, null, 4));
      })
      .finally(() => this.setState({ isLoading: false }));
  };

  getCompanyDirectors = (): void => {
    this.setState({ isLoading: true });
    request({
      query: GET_COMPANY_DIRECTORS,
      variables: { companyId: this.props.company.id },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            company: {
              directors: Director[];
            };
          };
        }>): void => {
          if (data.data.company.directors) {
            this.setState({
              companyDirectors: data.data.company.directors,
              isLoading: false,
            });
          }
        },
      )
      .catch((error) => {
        message.error("Could Not Fetch Company Directors");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  editCompanyContact = async (contactData: CompanyContact): Promise<void> => {
    this.setState(
      {
        currentCompanyContact: contactData,
        customerSearchTerm: contactData?.customer?.petraId ?? "",
        showCompanyContactModal: true,
        isExistingCustomer: !!contactData?.customer,
        documentUrl: contactData?.documentUrl || "",
      },
      () => {
        this.triggerCustomerSearch();
        this.handleIsExistingCustomerchecked(!!contactData?.customer);
        this.newCompanyContactPersonFormReference?.current?.setFieldsValue({
          contactName: contactData?.contactName,
          emailAddress: contactData?.emailAddress,
          mobileNumber: contactData?.mobileNumber,
          contactLevelIds: contactData?.contactLevels?.map((level) => level.id),
          role: contactData?.role,
          isExistingCustomer: contactData?.customer ? true : false,
          documentUrl: contactData?.documentUrl,
          customerId: contactData?.customer?.id,
        });
      },
    );
  };

  toggleCompanyContactModal = (open?: boolean): void => {
    this.setState(
      (prevState) => ({
        showCompanyContactModal:
          open !== undefined ? open : !prevState.showCompanyContactModal,
      }),
      () => {
        if (this.state.showCompanyContactModal && !this.state.currentCompanyContact) {
          this.newCompanyContactPersonFormReference?.current?.resetFields();
        }
        if (!this.state.showCompanyContactModal) {
          this.newCompanyContactPersonFormReference?.current?.resetFields();
          this.setState({
            currentCompanyContact: undefined,
            customerSearchTerm: "",
            isExistingCustomer: false,
            documentUrl: "",
            companyContactFieldsToHide: ["customerId"],
          });
        }
      },
    );
  };

  editCompanyDirector = (director: Director): void => {
    this.setState({
      currentCompanyDirector: director,
      showDirectorContactForm: true
    });
  };

  updateCompanyEmail = async ({
    companyEmail,
    newlyCreatedEmailId,
  }: UpdateCompanyEmailFnProps): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        updateCompanyEmail: {
          companyEmail: {
            company: Company;
            email: EmailInterface;
          };
        };
      };
    }

    request({
      query: UPDATE_COMPANY_EMAIL,
      variables: {
        companyEmailId: companyEmail.id,
        emailId: newlyCreatedEmailId,
        companyId: this.props.company.id,
      },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      if (data.updateCompanyEmail.companyEmail.company) {
        this.setState({
          isLoading: false,
          emailList: data.updateCompanyEmail.companyEmail.company.companyEmails,
        });
      } else {
        message.error("Request Failed");
        this.setState({ isLoading: false });
      }
    });
  };

  updateCompanyPhone = async ({
    companyPhone,
    newlyCreatedPhoneId,
  }: UpdateCompanyPhoneFnProps): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        updateCompanyPhone: {
          companyPhone: {
            company: Company;
            phone: PhoneInterface;
          };
        };
      };
    }

    request({
      query: UPDATE_COMPANY_PHONE,
      variables: {
        companyPhoneId: companyPhone.id,
        phoneId: newlyCreatedPhoneId,
        companyId: this.props.company.id,
      },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      if (
        data.updateCompanyPhone.companyPhone &&
        data.updateCompanyPhone.companyPhone.company
      ) {
        this.setState({
          isLoading: false,
          phoneList: data.updateCompanyPhone.companyPhone.company.companyPhones,
        });
      } else {
        message.error("Request Failed");
        this.setState({ isLoading: false });
      }
    });
  };

  setCustomerCompanyAssociationToBeEdited = (
    customerCompanyAssociationBeingEdited: CustomerCompanyAssociation,
  ): void => {
    this.setState({ customerCompanyAssociationBeingEdited, showForm: true });
  };

  resetState = async (): Promise<void> => {
    this.reference.current?.resetFields(customerFieldsToBeValidated);
    this.lookupFormReference.current?.resetFields(customerFieldsToBeValidated);
    this.setState({
      showForm: false,
      customerCompanyAssociationBeingEdited: undefined,
      lookupPhoneNumber: undefined,
      existingUserFound: false,
      isLoading: false,
    });
  };

  assignCustomerToCompany = async ({
    customerId,
    relationship,
    staffId,
    influenceLevel,
    isEmployee,
    dateOfEmployment,
    customerContactLevels,
  }: {
    customerId: string;
    relationship?: string;
    staffId?: string;
    influenceLevel?: string;
    isEmployee?: boolean;
    dateOfEmployment?: string;
    customerContactLevels?: string[];
  }): Promise<void> => {
    const { company } = this.props;
    const { customerCompanyAssociations, contactPersons } = this.state;

    interface Response {
      data: {
        createCustomerCompanyAssociation: {
          customerCompanyAssociation: CustomerCompanyAssociation;
        };
        errors: [
          {
            property: string;
            description: string;
          },
        ];
      };
    }

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<Response> = await request({
        query: ASSIGN_CUSTOMER_TO_COMPANY_MUTATION,
        variables: {
          customerId,
          relationship: relationship,
          staffId,
          companyId: company.id,
          influenceLevel: influenceLevel,
          isEmployee: isEmployee,
          dateOfEmployment: dateOfEmployment,
          contactLevelIds: customerContactLevels,
        },
      });

      const result = data.data.createCustomerCompanyAssociation;

      if (!result.customerCompanyAssociation) {
        throw new Error("Failed to assign user to company");
      }
      const newCustomerCompanyAssociation = result.customerCompanyAssociation;
      const customer = newCustomerCompanyAssociation.customer;
      if (contactPersons === undefined) {
        this.setState({
          customerCompanyAssociations: [
            ...customerCompanyAssociations,
            newCustomerCompanyAssociation,
          ],
        });
      } else {
        this.setState({
          customerCompanyAssociations: [
            ...customerCompanyAssociations,
            newCustomerCompanyAssociation,
          ],
          contactPersons: [...contactPersons, customer as Customer],
        });
      }

      await this.resetState();
    } catch (error) {
      if (!error.message.contains("contactPersons")) {
        message.error(error.message);
      }
    }

    this.setState({ isLoading: false });
  };

  async getCompanyAccountTags(): Promise<void> {
    interface Response {
      data: {
        tags: SelectOptionsInterface[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TAGS,
        variables: {
          forCompany: true,
          forAccount: true,
        },
      });

      this.setState({
        companyAccountTags: data.tags,
        isLoading: false,
      });
    } catch (error) { }
  }

  async getProductTags(): Promise<void> {
    interface Response {
      data: {
        tags: SelectOptionsInterface[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TAGS,
        variables: {
          forProduct: true,
        },
      });

      this.setState({
        productTags: data.tags.filter((tag) => !isAutomaticTag(tag.name)),
        isLoading: false,
      });
    } catch (error) { }
  }

  handleCompanyTagChange = async (value: string): Promise<void> => {
    const values = value as unknown as string[];

    const selectedManualTag = this.state.companyAccountTags.find(
      (t) => values.includes(t.id) && MANUAL_TAG_NAMES.includes(t.name),
    );

    if (selectedManualTag) {
      this.setState({
        showTagDateModal: true,
        pendingTagIds: values,
        tagEffectiveDate: null,
        tagNotes: "",
      });
      return;
    }

    await this.submitTagAssignment(values, null);
  };

  submitTagAssignment = async (
    tagIds: string[],
    effectiveDate: moment.Moment | null,
    notes?: string,
  ): Promise<void> => {
    interface Response {
      customer: Company;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: ASSIGN_TAGS_TO_COMPANY,
        variables: {
          companyId: this.props.company.id,
          tagIds,
          effectiveDate: effectiveDate ? effectiveDate.toISOString() : undefined,
          notes,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignTagsToCompany",
      );

      if (err) {
        message.error(`Failed to assign tag - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  handleTagDateModalConfirm = async (): Promise<void> => {
    const { pendingTagIds, tagEffectiveDate, tagNotes } = this.state;
    this.setState({ showTagDateModal: false });
    await this.submitTagAssignment(pendingTagIds!, tagEffectiveDate, tagNotes || undefined);
    this.setState({ pendingTagIds: null, tagEffectiveDate: null, tagNotes: "" });
  };

  handleTagDateModalCancel = (): void => {
    const originalTagIds = this.props.company.tags.map((t) => t.id);
    this.tagsReference.current?.setFieldsValue({ tags: originalTagIds });
    this.setState({
      showTagDateModal: false,
      pendingTagIds: null,
      tagEffectiveDate: null,
      tagNotes: "",
    });
  };

  assignTagToCompanySubscription = async (
    tagIds: string[],
    subscriptionId: string,
  ): Promise<void> => {
    interface Response {
      companySubscription: Subscription;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: ASSIGN_TAGS_TO_COMPANY_SUBSCRIPTION,
        variables: {
          companySubscriptionId: subscriptionId,
          tagIds,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignTagsToCompanySubscription",
      );

      if (err) {
        message.error(`Failed to assign tag - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  handleSubscriptionTagChange = (subscriptionId: string) => {
    return (tagIds: string): Promise<void> => {
      const ids = tagIds as unknown as string[];
      return this.assignTagToCompanySubscription(ids, subscriptionId);
    };
  };

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  setIdToBeDeleted = (idToBeDeleted: string): void => {
    this.setState({ idToBeDeleted });
  };

  toggleDeleteConfirmationModal = (companyContactIdSelected?: string): void => {
    this.setState((prevState) => ({
      companyContactIdSelected,
      showDeleteConfirmationModal: !prevState.showDeleteConfirmationModal,
    }));
  };

  toggleDeleteDirectorConfirmationModal = (directorIdSelected?: string): void => {
    this.setState((prevState) => ({
      directorIdSelected,
      showDeleteDirectorConfirmationModal: !prevState.showDeleteDirectorConfirmationModal,
    }));
  };

  deleteContactPerson = async (): Promise<void> => {
    const { idToBeDeleted } = this.state;

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    this.setState({
      isLoading: true,
      showDeleteConfirmationModal: false,
    });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_CONTACT_PERSON_MUTATION,
        variables: {
          id: idToBeDeleted,
        },
      });

      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "deleteCustomerCompanyAssociation",
      );

      if (mutation?.result) {
        const updatedList = this.state.customerCompanyAssociations.filter(
          (contactPerson: CustomerCompanyAssociation): boolean => {
            return contactPerson.id !== idToBeDeleted;
          },
        );

        this.setState({ customerCompanyAssociations: updatedList });
      } else {
        message.error("Failed to delete contact person");
      }
    } catch (error) {
      message.error(`Failed to delete contact person - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  updateCustomerCompanyAssociation = async ({
    relationship,
    staffId,
    influenceLevel,
    isEmployee,
    dateOfEmployment,
    contactLevelIds,
  }: {
    relationship: string;
    staffId: string;
    influenceLevel: string;
    isEmployee: boolean;
    dateOfEmployment: string;
    contactLevelIds: string[];
  }): Promise<void> => {
    const { customerCompanyAssociationBeingEdited } = this.state;
    interface Response {
      data: {
        updateCustomerCompanyAssociation: {
          customerCompanyAssociation: {
            company: Company;
          };
        };
      };
    }

    try {
      const {
        data: {
          data: { updateCustomerCompanyAssociation },
        },
      }: AxiosResponse<Response> = await request({
        query: UPDATE_CUSTOMER_COMPANY_ASSOCIATION_EXTENDED,
        variables: {
          id: customerCompanyAssociationBeingEdited?.id,
          companyId: this.props.company.id,
          customerId: customerCompanyAssociationBeingEdited?.customer.id,
          relationship,
          staffId,
          influenceLevel,
          isEmployee,
          dateOfEmployment,
          contactLevelIds,
        },
      });

      if (
        !updateCustomerCompanyAssociation ||
        !updateCustomerCompanyAssociation.customerCompanyAssociation ||
        !updateCustomerCompanyAssociation.customerCompanyAssociation.company
      ) {
        message.error("Failed to update contact person");
      }

      const company =
        updateCustomerCompanyAssociation.customerCompanyAssociation.company;

      const contactPersons = company.contactPersons.map(
        (customerCompanyAssociation: CustomerCompanyAssociation): Customer =>
          customerCompanyAssociation.customer,
      ) as ContactPersonInterface[];

      this.setState({
        contactPersons: contactPersons,
        customerCompanyAssociations: company.contactPersons,
      });

      await this.resetState();
      console.log("resetting");
    } catch (error) {
      message.error(error.message);
    }
  };

  updateCustomer = async (): Promise<void> => {
    const { currentContactPerson, customerCompanyAssociationBeingEdited } =
      this.state;
    const { editingState } = this.props;

    interface Response {
      customer: Customer;
    }

    if (!this.newContactPersonFormReference.current) return;

    let data;

    try {
      data = await this.newContactPersonFormReference.current.validateFields(
        customerFieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_CONTACT_PERSON_MUTATION,
        variables: {
          ...data,
          customerId: currentContactPerson && currentContactPerson.id,
          petraId: currentContactPerson && currentContactPerson.petraId,
        },
      });

      this.setState({ isLoading: false });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "updateContactPerson",
      );

      if (handled) {
        message.error("An Error Occurred.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.newContactPersonFormReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
      } else {
        const customer = mutation.customer;
        if (editingState && customerCompanyAssociationBeingEdited) {
          await this.updateCustomerCompanyAssociation({
            relationship: data.relationship,
            staffId: data.staffId,
            influenceLevel: data.influenceLevel,
            isEmployee: data.isEmployee,
            dateOfEmployment: data.dateOfEmployment,
            contactLevelIds: data.contactLevelIds,
          });
        } else {
          await this.assignCustomerToCompany({
            customerId: customer.id,
            relationship: data.relationship,
            staffId: data.staffId,
            influenceLevel: data.influenceLevel,
            isEmployee: data.isEmployee,
            dateOfEmployment: data.dateOfEmployment,
            customerContactLevels: data.contactLevelIds,
          });
        }
      }
    } catch (error) {
      message.error(`Request Failed - ${generateDisplayErrorMessage(error)}`);
      this.setState({ isLoading: false });
      Sentry.captureException(error);
    }
  };

  async getTrustees(): Promise<void> {
    interface Response {
      data: {
        trustees: SelectOptionsInterface[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TRUSTEES_SUMMARY,
      });

      this.setState({
        trustees: data.trustees,
        isLoading: false,
      });
    } catch (error) { }
  }

  getAllUsers = (): void => {
    request({
      query: GET_ROLE_SUMMARY,
      variables: {
        id: relationshipManagerRoleId,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.role?.users,
        isLoading: false,
      });
    });
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm: userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  triggerCustomerSearch = async (): Promise<void> => {
    const { customerSearchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customers: mutation.results,
        });
      }
    } catch (error) {
      message.error(`customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  triggerCustomerDelayedSearch = debounce(async (): Promise<void> => {
    await this.triggerCustomerSearch();
  }, DEBOUNCE_TIME);

  handleCustomerSearchInput = async (
    customerSearchTerm: string,
  ): Promise<void> => {
    this.setState({ customerSearchTerm });
    await this.triggerCustomerDelayedSearch();
  };

  async getIndustrySectors(): Promise<void> {
    interface Response {
      data: {
        industrySectors: SelectOptionsInterface[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_INDUSTRY_SECTORS,
      });

      this.setState({
        industrySectors: data.industrySectors,
        isLoading: false,
      });
    } catch (error) { }
  }

  lookupCustomer = async (): Promise<void> => {
    const fieldsToBeValidated = ["lookupPhoneNumber"];

    interface Response {
      customer: Customer;
      isNewEntity: boolean;
    }

    if (!this.lookupFormReference.current) return;

    this.setState({ isLoading: true });

    try {
      const validatedData =
        await this.lookupFormReference.current.validateFields(
          fieldsToBeValidated,
        );
      const isValid = await this.validatePhoneNumber(
        validatedData.lookupPhoneNumber,
      );
      if (!isValid) {
        message.error("Phone number validation failed");
        this.lookupFormReference.current.setFields([
          {
            name: "lookupPhoneNumber",
            errors: ["Phone Number Is Invalid"],
          },
        ]);
        this.setState({ isLoading: false });
        this.resetState();
        return;
      }
      const response: AxiosResponse = await request({
        query: LOOKUP_USER,
        variables: { phoneNumber: validatedData.lookupPhoneNumber },
      });

      const { mutation, handled, err } =
        responseHandlerForMutationsThatDoNotExposeErrors<Response>(
          response,
          "lookupCustomer",
        );

      if (handled || err) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else {
        const customer = mutation.customer;
        if (customer && mutation.isNewEntity) {
          this.setState({
            showForm: true,
            isLoading: false,
            currentContactPerson: customer,
            lookupPhoneNumber: validatedData.lookupPhoneNumber,
            customerCompanyAssociationBeingEdited: undefined,
          });
          return;
        } else {
          this.setState({
            currentContactPerson: customer,
            existingUserFound: true,
            isLoading: false,
          });
        }
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Request Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.resetState();
    }
  };

  assignPhoneToCompany = (phoneId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignPhoneToCompany: {
          companyPhone: AssociatedPhone;
        };
      };
    }

    return request({
      query: ASSIGN_PHONE_TO_COMPANY,
      variables: { phoneId, companyId: this.props.company.id },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      const companyPhone = data.assignPhoneToCompany.companyPhone;
      if (companyPhone) {
        const phoneExists = this.phoneAlreadyExists(companyPhone.id);
        if (!phoneExists) {
          this.setState({ phoneList: [...this.state.phoneList, companyPhone] });
          message.success("Phone number added successfully");
        } else {
          message.error("Phone number has already been added");
        }
      } else {
        message.error("Request Failed");
      }
      this.setState({ isLoading: false });
    });
  };

  assignAddressToCompany = (addressId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAddressToCompany: {
          companyAddress: AssociatedAddress;
        };
      };
    }

    return request({
      query: ASSIGN_ADDRESS_TO_COMPANY,
      variables: { addressId, companyId: this.props.company.id },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      const companyAddress = data.assignAddressToCompany.companyAddress;
      if (companyAddress) {
        const addressExists = this.addressAlreadyExists(companyAddress.id);
        if (!addressExists) {
          this.setState({
            addressList: [...this.state.addressList, companyAddress],
          });
          message.success("Address assigned successfully");
        } else {
          message.error("Address has already been added");
        }
      } else {
        message.error("Request Failed");
      }
      this.setState({ isLoading: false });
    });
  };

  assignEmailToCompany = (emailId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignEmailToCompany: {
          companyEmail: AssociatedEmail;
        };
      };
    }

    return request({
      query: ASSIGN_EMAIL_TO_COMPANY,
      variables: { emailId, companyId: this.props.company.id },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      const companyEmail = data.assignEmailToCompany.companyEmail;
      if (companyEmail) {
        const emailExists = this.emailAlreadyExists(companyEmail.id);
        if (!emailExists) {
          this.setState({ emailList: [...this.state.emailList, companyEmail] });
          message.success("Email assigned successfully");
        } else {
          message.error("Email has already been added");
        }
      } else {
        message.error("Request Failed");
      }
      this.setState({ isLoading: false });
    });
  };

  unAssignPhoneFromCompany = (phoneId: string): void => {
    const phoneList: AssociatedPhone[] = this.state.phoneList;

    const companyPhone: AssociatedPhone | undefined = phoneList.find(
      (companyPhone: AssociatedPhone): boolean => {
        return companyPhone.phone.id === phoneId;
      },
    );

    if (companyPhone) {
      this.setState({ isLoading: true });
      request({
        query: UNASSIGN_PHONE_FROM_COMPANY,
        variables: { companyPhoneId: companyPhone.id },
      }).then(({ data }: AxiosResponse<any>): void => {
        if (!data.errors && data.data.unassignPhoneFromCompany) {
          const updatedList = this.state.phoneList.filter(
            (element: AssociatedPhone): boolean => {
              return element.phone.id !== companyPhone.phone.id;
            },
          );

          this.setState({ phoneList: updatedList, isLoading: false });
        }
      });
    }
  };

  unAssignAddressFromCompany = (addressId: string): void => {
    const addressList: AssociatedAddress[] = this.state.addressList;

    const companyAddress: AssociatedAddress | undefined = addressList.find(
      (associatedAddress: AssociatedAddress): boolean => {
        return associatedAddress.address.id === addressId;
      },
    );

    if (companyAddress) {
      this.setState({ isLoading: true });
      request({
        query: UNASSIGN_ADDRESS_FROM_COMPANY,
        variables: { companyAddressId: companyAddress.id },
      }).then(({ data }: AxiosResponse<any>): void => {
        if (!data.errors && data.data.unassignAddressFromCompany) {
          const updatedList: AssociatedAddress[] = addressList.filter(
            (element: AssociatedAddress): boolean => {
              return element.address.id !== companyAddress.address.id;
            },
          );

          this.setState({ addressList: updatedList, isLoading: false });
        }
      });
    }
  };

  unAssignEmailFromCompany = (emailId: string): void => {
    const emailList: AssociatedEmail[] = this.state.emailList;

    const companyEmail: AssociatedEmail | undefined = emailList.find(
      (companyEmail: AssociatedEmail): boolean => {
        return companyEmail.email.id === emailId;
      },
    );

    if (companyEmail) {
      this.setState({ isLoading: true });
      request({
        query: UNASSIGN_EMAIL_FROM_COMPANY,
        variables: { companyEmailId: companyEmail.id },
      }).then(({ data }: AxiosResponse<any>): void => {
        if (!data.errors && data.data.unassignEmailFromCompany) {
          const updatedList = emailList.filter(
            (element: AssociatedEmail): boolean => {
              return element.email.id !== companyEmail.email.id;
            },
          );

          this.setState({ emailList: updatedList, isLoading: false });
        }
      });
    }
  };

  markEmailAsPrimary = (emailId: string): void => {
    this.setState({ isLoading: true });
    request({
      query: MARK_COMPANY_EMAIL_AS_PRIMARY,
      variables: { emailId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          markCompanyEmailAsPrimary: {
            companyEmails: AssociatedEmail[];
          };
        };
      }>): void => {
        const emailList: AssociatedEmail[] =
          data.data.markCompanyEmailAsPrimary.companyEmails;
        this.setState({ emailList, isLoading: false });
      },
    );
  };

  markAddressAsPrimary = (addressId: string): void => {
    this.setState({ isLoading: true });

    request({
      query: MARK_COMPANY_ADDRESS_AS_PRIMARY,
      variables: { addressId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          markCompanyAddressAsPrimary: {
            companyAddresses: AssociatedAddress[];
          };
        };
      }>): void => {
        const addressList: AssociatedAddress[] =
          data.data.markCompanyAddressAsPrimary.companyAddresses;
        this.setState({ addressList, isLoading: false });
      },
    );
  };

  markPhoneAsPrimary = (phoneId: string): void => {
    this.setState({ isLoading: true });

    request({
      query: MARK_COMPANY_PHONE_AS_PRIMARY,
      variables: { phoneId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          markCompanyPhoneAsPrimary: {
            companyPhones: AssociatedPhone[];
          };
        };
      }>): void => {
        const phoneList: AssociatedPhone[] =
          data.data.markCompanyPhoneAsPrimary.companyPhones;
        this.setState({ phoneList, isLoading: false });
      },
    );
  };

  async updateCompany<T>(variables: Record<string, unknown>): Promise<AxiosResponse<T>> {
    try {
      this.setState({ isLoading: true });

      const response: AxiosResponse<T> = await request({
        query: UPDATE_COMPANY_MUTATION,
        variables,
      });

      const { err } = responseHandler(response, "updateCompany");

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        message.success("Company updated successfully");
      }

      return response;
    } catch (error) {
      message.error(`Error updating company - ${generateDisplayErrorMessage(error)}`);
      throw error;
    } finally {
      this.setState({ isLoading: false });
    }
  }

  prev = (): void => {
    this.props.prev();
  };

  async next(): Promise<void> {
    const { currentContactPerson, companyContactPersons } = this.state;
    const { company } = this.props;

    const fieldsToBeValidated: string[] = [
      "tinNumber",
      "companyName",
      "industrySectorId",
      "numberOfEmployees",
      "trusteeId",
      "relationshipManagerId",
      "ssnitNumber",
      "rocNumber",
      "dateOfEstablishment",
      "companySegment"
    ];

    interface Response {
      company: Company;
    }

    if (!this.reference.current) return;

    const newOpportunityStage = window.location.pathname
      .split("/")
      .includes("new-opportunity");

    if (!newOpportunityStage) {
      if (!currentContactPerson) {
        if (companyContactPersons.length < 1) {
          message.error("At least 1 contact Person required");
          return;
        }
      }
    }

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<any> = await this.updateCompany<Response>({
        ...data,
        name: data?.companyName ? data.companyName : company.name,
        petraId: company.petraId,
        companyId: company.id,
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateCompany",
      );

      if (handled) {
        message.error("An Error Occurred");
        return;
      }

      if (err) {
        const resolvedErrors: FieldData[] = [];
        err.forEach((err: APIError): void => {
          resolvedErrors.push({
            name: err.property,
            errors: [
              `${mapServerPropToLocalProp[
              err.property as keyof typeof mapServerPropToLocalProp
              ]
              } ${err.description}`,
            ],
          });
        });
        this.reference.current.setFields(resolvedErrors);
        message.error(
          `Error updating company: ${resolvedErrors[0].errors?.join(".")}`,
        );
      } else {
        if (this.props.editingState && this.props.save) {
          this.props.save();
        } else {
          this.props.next();
        }
      }
    } catch (error) {
      message.error(`Failed to update company - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  assignExistingCustomerToCompany = async (): Promise<void> => {
    const { currentContactPerson } = this.state;

    const fieldsToBeValidated: string[] = [
      "existingCustomerRelationship",
      "existingCustomerStaffId",
      "existingCustomerInfluenceLevel",
      "existingCustomerDateOfEmployment",
      "existingCustomerIsEmployee",
      "existingCustomerContactLevels",
    ];

    if (!this.existingContactPersonFormReference.current) return;

    let data;

    try {
      data =
        await this.existingContactPersonFormReference.current.validateFields(
          fieldsToBeValidated,
        );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    if (currentContactPerson) {
      this.assignCustomerToCompany({
        customerId: currentContactPerson.id,
        relationship: data.existingCustomerRelationship,
        staffId: data.existingCustomerStaffId,
        influenceLevel: data.existingCustomerInfluenceLevel,
        dateOfEmployment: data.existingCustomerDateOfEmployment,
        isEmployee: data.existingCustomerIsEmployee,
        customerContactLevels: data.existingCustomerContactLevels,
      });
    }
  };

  generateBranchMap = async (): Promise<void> => {
    const branchMap: BranchMap = {};

    this.state.financialInstitutions.forEach(
      (institution: FinancialInstitution): void => {
        branchMap[institution.id] = institution.bankBranches;
      },
    );

    this.setState({ branchMap });
  };

  async getFinancialInstitutionals(): Promise<void> {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
        isLoading: false,
      });
      this.generateBranchMap();
    } catch (error) { }
  }

  async getTitles(): Promise<void> {
    interface Response {
      data: {
        titles: SelectOptionsInterface[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TITLES,
      });

      this.setState({
        titles: data.titles,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get titles  - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  }

  async getContactLevels(): Promise<void> {
    interface Response {
      data: {
        contactLevels: ContactLevel[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_CONTACT_LEVELS,
      });

      this.setState({
        contactLevels: data.contactLevels,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get contact levels  - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  }

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    const { setPercentage, company } = this.props;
    setPercentage(50);
    await Promise.all([
      this.getIndustrySectors(),
      this.getTrustees(),
      this.getCompanyAccountTags(),
      this.getProductTags(),
      this.getFinancialInstitutionals(),
      this.getTitles(),
      this.getContactLevels(),
      this.getAllUsers(),
      this.getCompanyDirectors()
    ]);

    if (window.location.pathname.split("/").includes("business-development")) {
      this.setState({
        contactPersonRequired: false,
      });
    }

    this._isMounted &&
      this.setState({
        emailList: company.companyEmails,
        phoneList: company.companyPhones,
        addressList: company.companyAddresses,
        contactPersons: company.customers as Customer[],
        customerCompanyAssociations: company.contactPersons,
        companyContactPersons: this.props.companyContacts ?? [],
        isLoading: false,
      });
  }

  render(): JSX.Element {
    const { company, countries, editingState } = this.props;

    const {
      phoneList,
      emailList,
      addressList,
      isLoading,
      industrySectors,
      trustees,
      showDeleteConfirmationModal,
      financialInstitutions,
      branchMap,
      contactLevels,
      users,
      customers,
      currentCompanyContact,
      companyContactPersons,
      companyDirectors,
      showDirectorContactForm,
      showDeleteDirectorConfirmationModal,
      contactPersonRequired,
      showCompanyContactModal
    } = this.state;
    const { isEditing } = this.props;

    let contactLevelsIds: string[] = [];
    if (
      currentCompanyContact &&
      currentCompanyContact.contactLevels.length > 0
    ) {
      const associationLevelsArray = currentCompanyContact.contactLevels.map(
        (cclevel) => cclevel.id,
      );
      contactLevelsIds = associationLevelsArray;
    }

    const companyDetailsConfig: FormFieldConfig[] = [
      {
        id: "companyName",
        name: "companyName",
        inputType: "string",
        rule: "string",
        initialValue: company.name,
        required: true,
        placeholder: "Company Name",
        label: "Company Name",
        colConfig: {
          span: 8,
        },
      },

      {
        id: "tinNumber",
        name: "tinNumber",
        inputType: "string",
        rule: "string",
        initialValue: company.tinNumber,
        required: false,
        placeholder: "Tin",
        label: "TIN",
        colConfig: {
          span: 8,
        },
      },
      {
        id: "industrySectorId",
        name: "industrySectorId",
        inputType: "select",
        rule: "string",
        initialValue: company.industrySector
          ? company.industrySector.id
          : undefined,
        required: false,
        placeholder: "Industry Sector",
        label: "Industry Sector",
        selectOptions: industrySectors,
        colConfig: {
          span: 8,
        },
      },
      {
        id: "numberOfEmployees",
        name: "numberOfEmployees",
        inputType: "number",
        rule: "integer",
        initialValue: company.numberOfEmployees,
        required: false,
        placeholder: "Number of Employees",
        label: "No. of Employees",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 8,
        },
      },
      {
        id: "trusteeId",
        name: "trusteeId",
        inputType: "select",
        initialValue: company.trustee ? company.trustee.id : undefined,
        required: false,
        placeholder: "Former Trustee",
        rule: "string",
        label: "Trustee",
        selectOptions: trustees,
        colConfig: {
          span: 8,
        },
      },
      {
        id: "relationshipManagerId",
        name: "relationshipManagerId",
        inputType: "search",
        initialValue: company.relationshipManager?.id,
        required: false,
        placeholder: "Relationship Manager",
        rule: "string",
        label: "Relationship Manager",
        selectOptions: users,
        changeHandler: this.handleUserSearchInput,
        colConfig: {
          span: 8,
        },
      },
      {
        id: "ssnitNumber",
        name: "ssnitNumber",
        inputType: "string",
        rule: "string",
        initialValue: company?.ssnitNumber,
        required: isEditing,
        placeholder: "SSNIT Number",
        label: "SSNIT Number",
        colConfig: {
          span: 8,
        },
      },
      {
        id: "rocNumber",
        name: "rocNumber",
        inputType: "string",
        rule: "string",
        initialValue: company?.rocNumber,
        required: isEditing,
        placeholder: "RDG/ROC Number",
        label: "RDG/ROC Number",
        colConfig: {
          span: 8,
        },
      },
      {
        id: "dateOfEstablishment",
        name: "dateOfEstablishment",
        inputType: "defaultDate",
        rule: "object",
        required: false,
        initialValue: company?.dateOfEstablishment ? moment(company?.dateOfEstablishment) : "",
        label: "Date Of Establishment",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 8,
        },
      },
      {
        id: "companySegment",
        name: "companySegment",
        inputType: "select",
        rule: "string",
        initialValue: company?.companySegment,
        required: false,
        placeholder: "Company Segment",
        selectOptions: generateCompanySegments(),
        label: "Company Segment",
        colConfig: {
          span: 8,
        },
      },
    ];

    const isExistingCustomerValue =
      currentCompanyContact?.customer ? true : this.state.isExistingCustomer;
    const documentColSpan = isExistingCustomerValue ? 11 : 24;
    const documentDefaultFileList = currentCompanyContact?.documentUrl
      ? [
          {
            uid: `${currentCompanyContact.id}-document`,
            name: extractFileName(currentCompanyContact.documentUrl),
            url: currentCompanyContact.documentUrl,
          },
        ]
      : [];
    const companyContactPersonFormFields: FormFieldConfig[] = [
      {
        id: "contactName",
        name: "contactName",
        inputType: "string",
        rule: "string",
        placeholder: "Contact Name",
        label: "Contact Name",
        required: true,
        initialValue: currentCompanyContact?.contactName,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "emailAddress",
        name: "emailAddress",
        inputType: "string",
        rule: "string",
        placeholder: "Email Address",
        label: "Email Address",
        required: true,
        initialValue: currentCompanyContact?.emailAddress,
        colConfig: {
          span: 11,
          offset: 1,
        },
      },
      {
        id: "mobileNumber",
        name: "mobileNumber",
        inputType: "string",
        rule: "string",
        placeholder: "Mobile Number",
        label: "Mobile Number",
        required: true,
        initialValue: currentCompanyContact?.mobileNumber,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "contactLevelIds",
        name: "contactLevelIds",
        inputType: "multipleSelect",
        rule: "string",
        placeholder: "Contact Level",
        label: "Contact Level",
        selectOptions: contactLevels,
        isArrayField: true,
        required: true,
        selectHandler: this.handleContactLevelsChange,
        initialValue: contactLevelsIds,
        colConfig: {
          span: 11,
          offset: 1,
        },
      },
      {
        id: "role",
        name: "role",
        inputType: "string",
        rule: "string",
        placeholder: "Role",
        label: "Role",
        required: true,
        initialValue: currentCompanyContact?.role,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "isExistingCustomer",
        name: "isExistingCustomer",
        inputType: "checkbox",
        required: false,
        rule: "boolean",
        label: "Is Existing Customer ?",
        checkboxChangeHandler: (e) =>
          this.handleIsExistingCustomerchecked(e.target.checked),
        checkboxCheckedValue: isExistingCustomerValue,
        colConfig: {
          span: 11,
          offset: 1
        },
      },
      {
        id: "documentUrl",
        name: "documentUrl",
        label: "Upload Document",
        inputType: "fileUpload",
        defaultFileList: documentDefaultFileList,
        placeholder: "Upload Document",
        rule: "string",
        required: contactPersonRequired,
        changeHandler: (combinedInfo: string) => this.handleFileUploadChange("documentUrl", combinedInfo),
        colConfig: {
          span: documentColSpan,
        },
      },
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder: "Seach by name or petraId",
        rule: "string",
        label: "Customer",
        selectOptions: convertCustomersToSelectOptions(customers),
        changeHandler: this.handleCustomerSearchInput,
        initialValue: currentCompanyContact?.customer?.id,
        colConfig: {
          span: 11,
          offset: isExistingCustomerValue ? 1 : 0,
        },
      },
    ];

    const companyDirectorFormFields: FormFieldConfig[] = [
      {
        id: "firstName",
        name: "firstName",
        inputType: "string",
        rule: "string",
        placeholder: "First Name",
        label: "First Name",
        required: true,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "lastName",
        name: "lastName",
        inputType: "string",
        rule: "string",
        placeholder: "Last Name",
        label: "Last Name",
        required: true,
        colConfig: {
          span: 11,
          offset: 1
        },
      },
    ];

    const companyDirectorContactFormFields: FormFieldConfig[] = [
      {
        id: "phoneNumber",
        name: "phoneNumber",
        inputType: "string",
        rule: "string",
        placeholder: "Phone Number",
        label: "Phone Number",
        required: false,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "emailAddress",
        name: "emailAddress",
        inputType: "string",
        rule: "string",
        placeholder: "Email Address",
        label: "Email Address",
        required: false,
        colConfig: {
          span: 11,
          offset: 1,
        },
      },
    ];

    const newOpportunityStage = window.location.pathname
      .split("/")
      .includes("new-opportunity");

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.props.getNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          style={{
            position: "relative",
            top: "-10px",
          }}
          cancelEdit={this.props.cancelEdit}
          config={{
            prev: this.prev,
            next: this.next,
            save: this.next,
            showNext: !editingState,
            showPrev: !editingState,
            showSave: editingState,
            showCancelEdit: true,
          }}
        >
          <Fragment>
            {ModalCreator({
              title: "Delete company contact",
              okType: "primary",
              okText: "Delete",
              showModal: showDeleteConfirmationModal,
              onOkay: this.deleteCompanyContact,
              toggleModal: () => this.toggleDeleteConfirmationModal(undefined),
              inscription: "Are you sure you want to delete this record ?",
            })}
            {ModalCreator({
              title: currentCompanyContact ? "Update Contact Person" : "Add Contact Person",
              okType: "primary",
              okText: currentCompanyContact ? "Update" : "Create",
              showModal: showCompanyContactModal,
              width: 700,
              onOkay: () => {
                currentCompanyContact
                  ? this.updateCompanyContact()
                  : this.createCompanyContact();
              },
              toggleModal: () => this.toggleCompanyContactModal(false),
              inscription: (
                <BaseForm
                  key={`company-contact-form-${currentCompanyContact?.id ?? "new"}`}
                  layout="vertical"
                  reference={this.newCompanyContactPersonFormReference}
                  fields={companyContactPersonFormFields}
                  fieldsToHide={this.state.companyContactFieldsToHide}
                />
              ),
            })}
            {ModalCreator({
              title: "Delete company director",
              okType: "primary",
              okText: "Delete",
              showModal: showDeleteDirectorConfirmationModal,
              onOkay: this.deleteCompanyDirector,
              toggleModal: () => this.toggleDeleteDirectorConfirmationModal(undefined),
              inscription: "Are you sure you want to delete this record ?",
            })}
            <BaseForm
              gutter={[24, 8]}
              fieldsToHide={[]}
              fields={companyDetailsConfig}
              size="large"
              layout="vertical"
              reference={this.reference}
            />
            {ModalCreator({
              title: "Enter Effective Date",
              okText: "Confirm",
              okType: "primary",
              okayButtonProps: { disabled: !this.state.tagEffectiveDate },
              showModal: this.state.showTagDateModal,
              onOkay: this.handleTagDateModalConfirm,
              toggleModal: this.handleTagDateModalCancel,
              inscription: (
                <div>
                  <p>Select the date this status change took effect:</p>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={this.state.tagEffectiveDate}
                    disabledDate={(d: moment.Moment) => d && d.isAfter(moment())}
                    onChange={(date: moment.Moment | null) => this.setState({ tagEffectiveDate: date })}
                  />
                  <div style={{ marginTop: 16 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontWeight: 500,
                      }}
                    >
                      Notes (Optional)
                    </label>
                    <Input.TextArea
                      value={this.state.tagNotes}
                      onChange={(event) =>
                        this.setState({ tagNotes: event.target.value })
                      }
                      placeholder="Add optional notes"
                      rows={3}
                    />
                  </div>
                </div>
              ),
            })}
            <Collapse
              style={{
                marginBottom: "12px",
              }}
            >
              {this.props.editingState &&
                this.props.subscriptions &&
                this.props.subscriptions.map((subscription: Subscription) => {
                  return (
                    <Panel
                      header={<span>{subscription.product.name}</span>}
                      key={subscription.id}
                    >
                      <BaseForm
                        layout="horizontal"
                        reference={this.tagsReference}
                        fields={[
                          {
                            isArrayField: true,
                            id: "tags",
                            name: "tags",
                            inputType: "multipleSelect",
                            label: "Tags",
                            rule: "string",
                            placeholder: "Select Tags",
                            required: false,
                            initialValue: subscriptionInitialTags(
                              subscription.companySubscriptionTags,
                            ),
                            changeHandler: this.handleSubscriptionTagChange(
                              subscription.id,
                            ),
                            colConfig: {
                              span: 24,
                            },
                            selectOptions: this.state.productTags,
                            elementStyle: {
                              marginBottom: "20px",
                            },
                          },
                        ]}
                        fieldsToHide={[]}
                      />
                    </Panel>
                  );
                })}
            </Collapse>
            <AssociatedEntitiesSection
              editingState={editingState}
              emailList={emailList}
              addressList={addressList}
              phoneList={phoneList}
              countries={countries}
              company={{
                company,
                updateCompanyPhone: this.updateCompanyPhone,
                updateCompanyEmail: this.updateCompanyEmail,
                updateCompanyAddress: this.updateCompanyAddress,
                assignEmailToCompany: this.assignEmailToCompany,
                assignAddressToCompany: this.assignAddressToCompany,
                assignPhoneToCompany: this.assignPhoneToCompany,
                unAssignAddressToCompany: this.unAssignAddressFromCompany,
                unAssignEmailToCompany: this.unAssignEmailFromCompany,
                unAssignPhoneToCompany: this.unAssignPhoneFromCompany,
                markEmailAsPrimary: this.markEmailAsPrimary,
                markAddressAsPrimary: this.markAddressAsPrimary,
                markPhoneAsPrimary: this.markPhoneAsPrimary,
                reactivatePhone: this.assignPhoneToCompany,
                reactivateEmail: this.assignEmailToCompany,
                reactivateAddress: this.assignAddressToCompany
              }}
            >
              {!newOpportunityStage && (
                <>
                  <Panel
                    header={
                      <span>
                        Company Contact Persons{" "}
                        <Badge
                          count={companyContactPersons?.length ?? 0}
                          style={{
                            backgroundColor: "#52c41a",
                            marginLeft: "8px",
                            verticalAlign: "middle",
                          }}
                        />
                      </span>
                    }
                    key="Contact Person"
                  >
                    <Spin spinning={false}>
                      <div style={{ marginBottom: "12px" }}>
                        <Button
                          type="primary"
                          onClick={() => {
                            this.setState(
                              {
                                currentCompanyContact: undefined,
                                customerSearchTerm: "",
                                isExistingCustomer: false,
                                documentUrl: "",
                              },
                              () => this.toggleCompanyContactModal(true),
                            );
                          }}
                          className="base-button"
                        >
                          <PlusOutlined /> Add Contact
                        </Button>
                      </div>
                      <Row>
                        <Col span={24}>
                          <Row gutter={[16, 16]}>
                            {companyContactPersons?.map((contactPersonItem) => {
                              const editbtn = (
                                <Row gutter={2}>
                                  <Button
                                    type="primary"
                                    onClick={() =>
                                      this.editCompanyContact(contactPersonItem)
                                    }
                                    className="base-button"
                                    style={{
                                      position: "relative",
                                      top: "10px",
                                      right: "5px",
                                      borderRadius: 0,
                                      height: "33px",
                                      marginRight: "15px"
                                    }}
                                  >
                                    <EditOutlined /> Edit
                                  </Button>
                                  <Button
                                    type="default"
                                    onClick={() =>
                                      this.toggleDeleteConfirmationModal(
                                        contactPersonItem.id,
                                      )
                                    }
                                    className="warning-btn"
                                    style={{
                                      position: "relative",
                                      top: "10px",
                                      right: "5px",
                                      borderRadius: 0,
                                      height: "33px",
                                    }}
                                  >
                                    <DeleteOutlined /> Delete
                                  </Button>
                                </Row>
                              );
                              return (
                                <Col span={8} key={contactPersonItem.id}>
                                  {CompanyContactPerson(
                                    contactPersonItem,
                                    undefined,
                                    editbtn,
                                  )}
                                </Col>
                              );
                            })}
                          </Row>
                        </Col>
                      </Row>
                    </Spin>
                  </Panel>

                  <Panel
                    header={
                      <span>
                        Company Directors{" "}
                        <Badge
                          count={companyDirectors?.length ?? 0}
                          style={{
                            backgroundColor: "#52c41a",
                            position: "relative",
                            top: "8px",
                            left: "7px",
                          }}
                        />
                      </span>
                    }
                    key="Director"
                  >
                    <Spin spinning={false}>
                      <Row>
                        <Col
                          span={16}
                          style={{
                            paddingRight: "20px",
                          }}
                        >
                          <h1>Director</h1>
                          <BaseForm
                            layout="vertical"
                            reference={this.newCompanyDirectorFormReference}
                            fields={companyDirectorFormFields}
                            fieldsToHide={[]}
                          />
                          <Button
                            type="primary"
                            onClick={() => {
                              this.createCompanyDirector();
                            }}
                            className="base-button"
                            style={{
                              position: "relative",
                              top: "10px",
                              right: "5px",
                              borderRadius: 0,
                              height: "33px",
                            }}
                          >
                            Create Director
                          </Button>

                          {
                            showDirectorContactForm && (
                              <div style={{ marginTop: "50px" }}>
                                <h1>Director Contact</h1>
                                <BaseForm
                                  layout="vertical"
                                  reference={this.newCompanyDirectorContactFormReference}
                                  fields={companyDirectorContactFormFields}
                                  fieldsToHide={[]}
                                />
                                <Button
                                  type="primary"
                                  onClick={() => {
                                    this.createCompanyDirectorContact();
                                  }}
                                  className="base-button"
                                  style={{
                                    position: "relative",
                                    top: "10px",
                                    right: "5px",
                                    borderRadius: 0,
                                    height: "33px",
                                  }}
                                >
                                  Add Contact
                                </Button>
                              </div>
                            )
                          }
                        </Col>
                        <Col>
                          {companyDirectors?.map((director) => {
                            const editbtn = (
                              <Row gutter={2}>
                                <Button
                                  type="primary"
                                  onClick={() =>
                                    this.editCompanyDirector(director)
                                  }
                                  className="base-button"
                                  style={{
                                    position: "relative",
                                    top: "10px",
                                    right: "5px",
                                    borderRadius: 0,
                                    height: "33px",
                                    marginRight: "15px"
                                  }}
                                >
                                  <EditOutlined /> Add Contact
                                </Button>
                                <Button
                                  type="default"
                                  onClick={() =>
                                    this.toggleDeleteDirectorConfirmationModal(
                                      director.id,
                                    )
                                  }
                                  className="warning-btn"
                                  style={{
                                    position: "relative",
                                    top: "10px",
                                    right: "5px",
                                    borderRadius: 0,
                                    height: "33px",
                                  }}
                                >
                                  <DeleteOutlined /> Delete
                                </Button>
                              </Row>
                            );
                            return (
                              <>
                                {CompanyDirector(
                                  director,
                                  undefined,
                                  editbtn,
                                  this.deleteDirectorContact
                                )}
                              </>
                            );
                          })}
                        </Col>
                      </Row>
                    </Spin>
                  </Panel>
                </>
              )}
            </AssociatedEntitiesSection>
            {this.props.showOnboardingDetailsSection && (
              <Fragment>
                <Divider />
                <CompanyOnboardingDetails
                  updateCompanyPaymentInformation={
                    this.props.updateCompanyPaymentInformation
                  }
                  financialInstitutions={financialInstitutions}
                  loadOpportunity={this.props.loadOpportunity}
                  opportunity={this.props.opportunity}
                  branchMap={branchMap}
                  isEditing={this.props.editingState || false}
                  company={this.props.company}
                />
              </Fragment>
            )}
          </Fragment>
        </StepContainer>
      </Spin>
    );
  }
}
